import dateFormat from 'dateformat';
import { cloneDeep } from 'lodash';
import { MutableRefObject } from 'react';
import { EarningsType } from './CYMAAPI';

export function parseStringNumber(input: string | number = 0): number {
  return typeof input === 'number' ? input : parseFloat(input);
}

export function getUTCDate(date: Date | string): Date {
  const cDate = typeof date === 'string' ? new Date(Date.parse(date)) : date;
  return new Date(Date.UTC(cDate.getUTCFullYear(), cDate.getUTCMonth(), cDate.getUTCDate()));
}

export function getWeekEndingDate(date: Date | string): Date {
  const utcDate = getUTCDate(date);
  return new Date(utcDate?.setDate(utcDate?.getDate() + (6 - utcDate?.getUTCDay())));
}

export function roundToDigit(value: string | number, digits: number = 0): number {
  let n: number;
  if (typeof value === 'string') {
    n = parseFloat(value);
  } else if (typeof value === 'number') {
    n = value;
  } else return NaN;

  var multiplicator = Math.pow(10, digits);
  let rounded = (Math.round((n + Number.EPSILON) * multiplicator) / multiplicator).toFixed(digits);

  return parseFloat(rounded);
}

export const dateFormatter =
  (format = 'mm/dd/yyyy') =>
  (params) => {
    var dateParsed = new Date(Date.parse(params.value));
    return params.value !== undefined && params.value !== null && params.value !== '' && dateParsed && !isNaN(dateParsed.valueOf())
      ? dateFormat(dateParsed, format)
      : params.value;
  };

export const decimalFormatter =
  (decimals = 2) =>
  (params) => {
    return params.value !== undefined && params.value !== null && params.value !== '' ? formatNumber(params.value, decimals) : params.value;
  };

export function currencyFormatter(params) {
  return params.value !== undefined && params.value !== null && params.value !== '' ? (params.value ? '$' : '') + formatNumber(params.value) : params.value;
}

export function formatCurrency(value) {
  return value !== undefined && value !== null && value !== '' ? (value !== undefined ? '$' : '') + formatNumber(value) : value;
}

export function formatNumber(number?: number | string, decimals: number = 2): string {
  return number !== undefined
    ? parseStringNumber(number)
        .toFixed(decimals)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
    : '';
}

export function getUnique(a: (string | number)[]): (string | number)[] {
  var seen = {};
  return a.filter((item) => {
    return seen.hasOwnProperty(item) ? false : (seen[item] = true);
  });
}

export function getUniqueObj<T>(a: T[], getId: (obj: T) => string): T[] {
  var seen = {};
  return a.filter((item) => {
    return seen.hasOwnProperty(getId(item)) ? false : (seen[getId(item)] = true);
  });
}

export function partition<T>(arr: T[], isValid: (elem: T) => boolean) {
  return arr.reduce(([pass, fail], elem) => (isValid(elem) ? [[...pass, elem], fail] : [pass, [...fail, elem]]), [[] as T[], [] as T[]]);
}

export function unCamelCase(str: string | undefined): string | undefined {
  return (
    str // insert a space before all single caps
      ?.replace(/([A-Z][a-z])/g, ' $1')
      // uppercase the first character
      .replace(/^./, function (str) {
        return str.toUpperCase();
      })
  );
}

export function merge<T>(elem: T, arr: T[], key: string | ((e: T) => any) = 'id', cloneArr: boolean = true): T[] {
  let clone = cloneArr ? cloneDeep(arr) : arr;
  const getKey = (e) => (typeof key === 'string' ? e?.[key] : key(e));
  let index = clone.findIndex((c) => getKey(c) === getKey(elem));
  if (index > -1) clone.splice(index, 1, elem);
  else clone.push(elem);
  return clone;
}

export function remove<T>(elem: T, arr: T[], key: string | ((e: T) => any) = 'id', cloneArr: boolean = true): T[] {
  let clone = cloneArr ? cloneDeep(arr) : arr;
  const getKey = (e) => (typeof key === 'string' ? e?.[key] : key(e));
  let index = clone.findIndex((c) => getKey(c) === getKey(elem));
  if (index > -1) clone.splice(index, 1);
  return clone;
}

export const combinedRef = (refs: (MutableRefObject<any> | ((instance: HTMLDivElement | null) => void))[]) => (e) => {
  refs.forEach((ref) => {
    if (typeof ref === 'function') {
      ref(e);
    } else if (ref) {
      ref.current = e;
    }
  });
};

export const isUUID = (val: string) => {
  return val.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i) !== null;
};

export function isNumeric(str: string | number) {
  return (
    !isNaN(str as unknown as number) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseStringNumber(str))
  ); // ...and ensure strings of whitespace fail
}

export const sortEarningsTypes = (et: EarningsType[]): EarningsType[] => {
  return et.sort((a, b) => {
    if (a.shortName === b.shortName) return 0;
    // note the + converts a boolean to a number (1 or 0)
    return (
      +(b.shortName === 'Reg Hours') - +(a.shortName === 'Reg Hours') || +b.shortName.includes('OT') - +a.shortName.includes('OT') || b.shortName.localeCompare(a.shortName)
    );
  });
};
